/*
 * @adonisjs/redis
 *
 * (c) AdonisJS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import type { Redis, Cluster } from 'ioredis'

/**
 * Base methods are shared by a regular Redis connection and
 * the cluster connection.
 * https://redis.github.io/ioredis/classes/Redis.html
 */
export const baseMethods = [
  'acl',
  'aclBuffer',
  'addBuiltinCommand',
  'append',
  'asking',
  'auth',
  'bgrewriteaof',
  'bgrewriteaofBuffer',
  'bgsave',
  'bitcount',
  'bitfield',
  'bitfield_ro',
  'bitop',
  'bitpos',
  'blmove',
  'blmoveBuffer',
  'blmpop',
  'blmpopBuffer',
  'blpop',
  'blpopBuffer',
  'brpop',
  'brpopBuffer',
  'brpoplpush',
  'brpoplpushBuffer',
  'bzmpop',
  'bzpopmax',
  'bzpopmaxBuffer',
  'bzpopmin',
  'bzpopminBuffer',
  'call',
  'callBuffer',
  'client',
  'clientBuffer',
  'cluster',
  'command',
  'config',
  'copy',
  'createBuiltinCommand',
  'dbsize',
  'decr',
  'decrby',
  'del',
  'discard',
  'dump',
  'dumpBuffer',
  'echo',
  'echoBuffer',
  'eval',
  'eval_ro',
  'evalsha',
  'evalsha_ro',
  'exec',
  'exists',
  'expire',
  'expireat',
  'expiretime',
  'failover',
  'fcall',
  'fcall_ro',
  'flushall',
  'flushdb',
  'function',
  'functionBuffer',
  'geoadd',
  'geodist',
  'geodistBuffer',
  'geohash',
  'geohashBuffer',
  'geopos',
  'georadius',
  'georadius_ro',
  'georadiusbymember',
  'georadiusbymember_ro',
  'geosearch',
  'geosearchstore',
  'get',
  'getBuffer',
  'getBuiltinCommands',
  'getbit',
  'getdel',
  'getdelBuffer',
  'getex',
  'getexBuffer',
  'getrange',
  'getrangeBuffer',
  'getset',
  'getsetBuffer',
  'hdel',
  'hello',
  'hexists',
  'hget',
  'hgetBuffer',
  'hgetall',
  'hgetallBuffer',
  'hincrby',
  'hincrbyfloat',
  'hincrbyfloatBuffer',
  'hkeys',
  'hkeysBuffer',
  'hlen',
  'hmget',
  'hmgetBuffer',
  'hmset',
  'hrandfield',
  'hrandfieldBuffer',
  'hscan',
  'hscanBuffer',
  'hscanBufferStream',
  'hscanStream',
  'hset',
  'hsetnx',
  'hstrlen',
  'hvals',
  'hvalsBuffer',
  'incr',
  'incrby',
  'incrbyfloat',
  'info',
  'keys',
  'keysBuffer',
  'lastsave',
  'latency',
  'lcs',
  'lindex',
  'lindexBuffer',
  'linsert',
  'llen',
  'lmove',
  'lmoveBuffer',
  'lmpop',
  'lmpopBuffer',
  'lolwut',
  'lpop',
  'lpopBuffer',
  'lpos',
  'lpush',
  'lpushx',
  'lrange',
  'lrangeBuffer',
  'lrem',
  'lset',
  'ltrim',
  'memory',
  'mget',
  'mgetBuffer',
  'migrate',
  'module',
  'move',
  'mset',
  'msetnx',
  'multi',
  'object',
  'persist',
  'pexpire',
  'pexpireat',
  'pexpiretime',
  'pfadd',
  'pfcount',
  'pfdebug',
  'pfmerge',
  'pfselftest',
  'ping',
  'pingBuffer',
  'pipeline',
  'psetex',
  'psync',
  'pttl',
  'pubsub',
  'randomkey',
  'randomkeyBuffer',
  'readonly',
  'readwrite',
  'rename',
  'renamenx',
  'replconf',
  'replicaof',
  'reset',
  'restore',
  'restore-asking',
  'role',
  'rpop',
  'rpopBuffer',
  'rpoplpush',
  'rpoplpushBuffer',
  'rpush',
  'rpushx',
  'sadd',
  'save',
  'scan',
  'scanBuffer',
  'scard',
  'script',
  'sdiff',
  'sdiffBuffer',
  'sdiffstore',
  'select',
  'set',
  'setBuffer',
  'setbit',
  'setex',
  'setnx',
  'setrange',
  'shutdown',
  'sinter',
  'sinterBuffer',
  'sintercard',
  'sinterstore',
  'sismember',
  'slaveof',
  'slowlog',
  'smembers',
  'smembersBuffer',
  'smismember',
  'smove',
  'sort',
  'sort_ro',
  'spop',
  'spopBuffer',
  'spublish',
  'srandmember',
  'srandmemberBuffer',
  'srem',
  'sscan',
  'sscanBuffer',
  'sscanBufferStream',
  'sscanStream',
  'strlen',
  'substr',
  'sunion',
  'sunionBuffer',
  'sunionstore',
  'swapdb',
  'sync',
  'time',
  'touch',
  'ttl',
  'type',
  'unlink',
  'unwatch',
  'wait',
  'watch',
  'xack',
  'xadd',
  'xaddBuffer',
  'xautoclaim',
  'xclaim',
  'xdel',
  'xgroup',
  'xinfo',
  'xlen',
  'xpending',
  'xrange',
  'xrangeBuffer',
  'xread',
  'xreadBuffer',
  'xreadgroup',
  'xrevrange',
  'xrevrangeBuffer',
  'xsetid',
  'xtrim',
  'zadd',
  'zaddBuffer',
  'zcard',
  'zcount',
  'zdiff',
  'zdiffBuffer',
  'zdiffstore',
  'zincrby',
  'zincrbyBuffer',
  'zinter',
  'zinterBuffer',
  'zintercard',
  'zinterstore',
  'zlexcount',
  'zmpop',
  'zmscore',
  'zmscoreBuffer',
  'zpopmax',
  'zpopmaxBuffer',
  'zpopmin',
  'zpopminBuffer',
  'zrandmember',
  'zrandmemberBuffer',
  'zrange',
  'zrangeBuffer',
  'zrangebylex',
  'zrangebylexBuffer',
  'zrangebyscore',
  'zrangebyscoreBuffer',
  'zrangestore',
  'zrank',
  'zrem',
  'zremrangebylex',
  'zremrangebyrank',
  'zremrangebyscore',
  'zrevrange',
  'zrevrangeBuffer',
  'zrevrangebylex',
  'zrevrangebylexBuffer',
  'zrevrangebyscore',
  'zrevrangebyscoreBuffer',
  'zrevrank',
  'zscan',
  'zscanBuffer',
  'zscanBufferStream',
  'zscanStream',
  'zscore',
  'zscoreBuffer',
  'zunion',
  'zunionBuffer',
  'zunionstore',
] satisfies (keyof Cluster)[]

/**
 * Methods available on a non-cluster Redis
 * connection
 */
export const redisMethods = [
  'acl',
  'aclBuffer',
  'addBuiltinCommand',
  'append',
  'asking',
  'auth',
  'bgrewriteaof',
  'bgrewriteaofBuffer',
  'bgsave',
  'bitcount',
  'bitfield',
  'bitfield_ro',
  'bitop',
  'bitpos',
  'blmove',
  'blmoveBuffer',
  'blmpop',
  'blmpopBuffer',
  'blpop',
  'blpopBuffer',
  'brpop',
  'brpopBuffer',
  'brpoplpush',
  'brpoplpushBuffer',
  'bzmpop',
  'bzpopmax',
  'bzpopmaxBuffer',
  'bzpopmin',
  'bzpopminBuffer',
  'call',
  'callBuffer',
  'client',
  'clientBuffer',
  'cluster',
  'command',
  'config',
  'copy',
  'createBuiltinCommand',
  'dbsize',
  'decr',
  'decrby',
  'del',
  'discard',
  'dump',
  'dumpBuffer',
  'echo',
  'echoBuffer',
  'eval',
  'eval_ro',
  'evalsha',
  'evalsha_ro',
  'exec',
  'exists',
  'expire',
  'expireat',
  'expiretime',
  'failover',
  'fcall',
  'fcall_ro',
  'flushall',
  'flushdb',
  'function',
  'functionBuffer',
  'geoadd',
  'geodist',
  'geodistBuffer',
  'geohash',
  'geohashBuffer',
  'geopos',
  'georadius',
  'georadius_ro',
  'georadiusbymember',
  'georadiusbymember_ro',
  'geosearch',
  'geosearchstore',
  'get',
  'getBuffer',
  'getBuiltinCommands',
  'getbit',
  'getdel',
  'getdelBuffer',
  'getex',
  'getexBuffer',
  'getrange',
  'getrangeBuffer',
  'getset',
  'getsetBuffer',
  'hdel',
  'hello',
  'hexists',
  'hget',
  'hgetBuffer',
  'hgetall',
  'hgetallBuffer',
  'hincrby',
  'hincrbyfloat',
  'hincrbyfloatBuffer',
  'hkeys',
  'hkeysBuffer',
  'hlen',
  'hmget',
  'hmgetBuffer',
  'hmset',
  'hrandfield',
  'hrandfieldBuffer',
  'hscan',
  'hscanBuffer',
  'hscanBufferStream',
  'hscanStream',
  'hset',
  'hsetnx',
  'hstrlen',
  'hvals',
  'hvalsBuffer',
  'incr',
  'incrby',
  'incrbyfloat',
  'info',
  'keys',
  'keysBuffer',
  'lastsave',
  'latency',
  'lcs',
  'lindex',
  'lindexBuffer',
  'linsert',
  'llen',
  'lmove',
  'lmoveBuffer',
  'lmpop',
  'lmpopBuffer',
  'lolwut',
  'lpop',
  'lpopBuffer',
  'lpos',
  'lpush',
  'lpushx',
  'lrange',
  'lrangeBuffer',
  'lrem',
  'lset',
  'ltrim',
  'memory',
  'mget',
  'mgetBuffer',
  'migrate',
  'module',
  'move',
  'mset',
  'msetnx',
  'multi',
  'object',
  'persist',
  'pexpire',
  'pexpireat',
  'pexpiretime',
  'pfadd',
  'pfcount',
  'pfdebug',
  'pfmerge',
  'pfselftest',
  'ping',
  'pingBuffer',
  'pipeline',
  'psetex',
  'psync',
  'pttl',
  'pubsub',
  'randomkey',
  'randomkeyBuffer',
  'readonly',
  'readwrite',
  'rename',
  'renamenx',
  'replconf',
  'replicaof',
  'reset',
  'restore',
  'restore-asking',
  'role',
  'rpop',
  'rpopBuffer',
  'rpoplpush',
  'rpoplpushBuffer',
  'rpush',
  'rpushx',
  'sadd',
  'save',
  'scan',
  'scanBuffer',
  'scard',
  'script',
  'sdiff',
  'sdiffBuffer',
  'sdiffstore',
  'select',
  'set',
  'setBuffer',
  'setbit',
  'setex',
  'setnx',
  'setrange',
  'shutdown',
  'sinter',
  'sinterBuffer',
  'sintercard',
  'sinterstore',
  'sismember',
  'slaveof',
  'slowlog',
  'smembers',
  'smembersBuffer',
  'smismember',
  'smove',
  'sort',
  'sort_ro',
  'spop',
  'spopBuffer',
  'spublish',
  'srandmember',
  'srandmemberBuffer',
  'srem',
  'sscan',
  'sscanBuffer',
  'sscanBufferStream',
  'sscanStream',
  'strlen',
  'substr',
  'sunion',
  'sunionBuffer',
  'sunionstore',
  'swapdb',
  'sync',
  'time',
  'touch',
  'ttl',
  'type',
  'unlink',
  'unwatch',
  'wait',
  'watch',
  'xack',
  'xadd',
  'xaddBuffer',
  'xautoclaim',
  'xclaim',
  'xdel',
  'xgroup',
  'xinfo',
  'xlen',
  'xpending',
  'xrange',
  'xrangeBuffer',
  'xread',
  'xreadBuffer',
  'xreadgroup',
  'xrevrange',
  'xrevrangeBuffer',
  'xsetid',
  'xtrim',
  'zadd',
  'zaddBuffer',
  'zcard',
  'zcount',
  'zdiff',
  'zdiffBuffer',
  'zdiffstore',
  'zincrby',
  'zincrbyBuffer',
  'zinter',
  'zinterBuffer',
  'zintercard',
  'zinterstore',
  'zlexcount',
  'zmpop',
  'zmscore',
  'zmscoreBuffer',
  'zpopmax',
  'zpopmaxBuffer',
  'zpopmin',
  'zpopminBuffer',
  'zrandmember',
  'zrandmemberBuffer',
  'zrange',
  'zrangeBuffer',
  'zrangebylex',
  'zrangebylexBuffer',
  'zrangebyscore',
  'zrangebyscoreBuffer',
  'zrangestore',
  'zrank',
  'zrem',
  'zremrangebylex',
  'zremrangebyrank',
  'zremrangebyscore',
  'zrevrange',
  'zrevrangeBuffer',
  'zrevrangebylex',
  'zrevrangebylexBuffer',
  'zrevrangebyscore',
  'zrevrangebyscoreBuffer',
  'zrevrank',
  'zscan',
  'zscanBuffer',
  'zscanBufferStream',
  'zscanStream',
  'zscore',
  'zscoreBuffer',
  'zunion',
  'zunionBuffer',
  'zunionstore',
  'end',
  'monitor',
  'scanBufferStream',
  'scanStream',
] satisfies (keyof Redis)[]
